"use client";

import { debounce, isFunction, isObject } from "@huub-web/utils";
import { useRef, useState } from "react";

type DebouncedCallback<T> = (state: T) => void;
type DebouncedCallbackObject<T> = {
  debounced: DebouncedCallback<T>;
  immediate: DebouncedCallback<T>;
};

/**
 * A hook that debounces the state changes and calls the callback function.
 */
export function useDebouncedState<T>(
  initialValue: T,
  callback: DebouncedCallback<T> | DebouncedCallbackObject<T>,
  delay: number = 250,
) {
  const [state, setState] = useState<T>(initialValue);

  const debouncedCallback = useRef(
    debounce(
      isFunction(callback)
        ? callback
        : isObject(callback)
          ? (callback as DebouncedCallbackObject<T>).debounced
          : () => {},
      delay,
    ),
  );

  const setDebouncedState = (state: T) => {
    setState(state);
    if (isObject(callback)) {
      (callback as DebouncedCallbackObject<T>).immediate(state);
    }
    debouncedCallback.current(state);
  };

  return {
    state,
    setState: setDebouncedState,
    setImmediateState: setState,
  };
}
